// frontend/src/dashboard/SupportPage.tsx
// Tenant support page — create tickets, view list, reply in thread.

import * as React from 'react';
import { PageHeader } from '../design-system/components/PageHeader';
import { LoadingState } from '../design-system/components/LoadingState';
import { ErrorState } from '../design-system/components/ErrorState';
import { VISUAL, statusPill, type StatusTone } from '../design-system/visual';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { ApiError } from '../core/api-client';
import {
  tenantSupportApi,
  type SupportTicket,
  type SupportMessage,
  type TicketTopic,
} from '../modules/support/api';

const TOPICS: Array<{ value: TicketTopic; label: string }> = [
  { value: 'general',   label: 'General question' },
  { value: 'billing',   label: 'Billing' },
  { value: 'technical', label: 'Technical issue' },
  { value: 'feature',   label: 'Feature request' },
];

function ticketTone(status: string): StatusTone {
  if (status === 'open')    return 'info';
  if (status === 'pending') return 'warning';
  return 'muted';
}

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

/* ─── New ticket form ─── */

function NewTicketForm({ onCreated }: { onCreated: (t: SupportTicket) => void }) {
  const [subject, setSubject] = React.useState('');
  const [topic,   setTopic]   = React.useState<TicketTopic>('general');
  const [message, setMessage] = React.useState('');
  const [busy,    setBusy]    = React.useState(false);
  const [err,     setErr]     = React.useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      const ticket = await tenantSupportApi.create({ subject: subject.trim(), topic, message: message.trim() });
      setSubject(''); setMessage(''); setTopic('general');
      onCreated(ticket);
    } catch (ex) {
      setErr(ex instanceof ApiError ? ex.message : String((ex as any)?.message ?? ex));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className={[VISUAL.bento, 'p-6'].join(' ')}>
      <h2 className="text-[15px] font-semibold text-foreground mb-4">New support ticket</h2>
      <form onSubmit={submit} className="space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="s-subject">Subject</Label>
            <Input id="s-subject" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Brief description" required />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="s-topic">Topic</Label>
            <select
              id="s-topic"
              value={topic}
              onChange={(e) => setTopic(e.target.value as TicketTopic)}
              className="flex h-10 w-full rounded-xl border border-input bg-background px-3 py-2 text-[13.5px] ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
            >
              {TOPICS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="s-message">Message</Label>
          <Textarea id="s-message" value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Describe your issue…" rows={4} required className="resize-none" />
        </div>
        {err && <p className="text-[13px] text-destructive">{err}</p>}
        <Button type="submit" disabled={busy || !subject.trim() || !message.trim()} className="rounded-full px-5 h-10 text-[13px] font-semibold">
          {busy ? 'Submitting…' : 'Submit ticket'}
        </Button>
      </form>
    </section>
  );
}

/* ─── Thread panel ─── */

function TicketThread({ ticket, onBack }: { ticket: SupportTicket; onBack: () => void }) {
  const [thread, setThread] = React.useState<{ ticket: SupportTicket; messages: SupportMessage[] } | null>(null);
  const [loadErr, setLoadErr] = React.useState<string | null>(null);
  const [reply,   setReply]   = React.useState('');
  const [sending, setSending] = React.useState(false);
  const [sendErr, setSendErr] = React.useState<string | null>(null);

  function load() {
    setLoadErr(null);
    setThread(null);
    tenantSupportApi.get(ticket.id)
      .then(setThread)
      .catch((e: unknown) => setLoadErr(e instanceof ApiError ? e.message : String((e as any)?.message ?? e)));
  }
  React.useEffect(() => { load(); }, [ticket.id]); // eslint-disable-line react-hooks/exhaustive-deps

  async function submitReply(e: React.FormEvent) {
    e.preventDefault();
    if (!reply.trim()) return;
    setSendErr(null);
    setSending(true);
    try {
      const msg = await tenantSupportApi.reply(ticket.id, reply.trim());
      setReply('');
      setThread((prev) => prev ? { ...prev, messages: [...prev.messages, msg] } : prev);
    } catch (ex) {
      setSendErr(ex instanceof ApiError ? ex.message : String((ex as any)?.message ?? ex));
    } finally {
      setSending(false);
    }
  }

  return (
    <section className={[VISUAL.bento, 'p-6'].join(' ')}>
      <div className="flex items-center gap-3 mb-5">
        <button type="button" onClick={onBack} className="text-[13px] text-muted-foreground hover:text-foreground transition-colors">← Back</button>
        <span className="text-[13px] font-semibold text-foreground truncate flex-1">{ticket.subject}</span>
        <span className={statusPill(ticketTone(thread?.ticket.status ?? ticket.status))}>{thread?.ticket.status ?? ticket.status}</span>
      </div>

      {loadErr && <ErrorState description={loadErr} onRetry={load} />}
      {!thread && !loadErr && <LoadingState variant="inline" />}

      {thread && (
        <div className="space-y-4">
          <ul className="space-y-3">
            {thread.messages.map((m) => (
              <li key={m.id} className={['rounded-2xl px-4 py-3 text-[13.5px] leading-relaxed', m.author_type === 'admin' ? 'bg-ink-900 text-white ml-6' : 'bg-muted/50 ring-1 ring-border/40 mr-6'].join(' ')}>
                <p className="text-[11px] font-semibold uppercase tracking-[0.10em] mb-1 opacity-60">{m.author_name} · {relativeTime(m.created_at)}</p>
                <p className="whitespace-pre-wrap">{m.body}</p>
              </li>
            ))}
          </ul>

          {ticket.status !== 'closed' && (
            <form onSubmit={submitReply} className="space-y-3 pt-2 border-t border-border/40">
              <Textarea
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                placeholder="Write a reply…"
                rows={3}
                className="resize-none"
              />
              {sendErr && <p className="text-[13px] text-destructive">{sendErr}</p>}
              <Button type="submit" disabled={sending || !reply.trim()} className="rounded-full px-5 h-9 text-[13px] font-semibold">
                {sending ? 'Sending…' : 'Send reply'}
              </Button>
            </form>
          )}
        </div>
      )}
    </section>
  );
}

/* ─── Main page ─── */

export function SupportPage() {
  const [tickets, setTickets] = React.useState<SupportTicket[] | null>(null);
  const [loadErr, setLoadErr] = React.useState<string | null>(null);
  const [selected, setSelected] = React.useState<SupportTicket | null>(null);

  function load() {
    setLoadErr(null);
    setTickets(null);
    tenantSupportApi.list()
      .then(setTickets)
      .catch((e) => setLoadErr(e instanceof ApiError ? e.message : String(e?.message ?? e)));
  }

  React.useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function handleCreated(t: SupportTicket) {
    setTickets((prev) => (prev ? [t, ...prev] : [t]));
    setSelected(t);
  }

  return (
    <div>
      <PageHeader
        eyebrow="Comms / Support"
        title="Support"
        description="Submit a ticket or continue an existing conversation with our team."
      />
      <div className="px-6 sm:px-8 lg:px-10 pb-12 space-y-6">

        {selected ? (
          <TicketThread ticket={selected} onBack={() => setSelected(null)} />
        ) : (
          <>
            <NewTicketForm onCreated={handleCreated} />

            <section>
              <h2 className="text-[13px] font-semibold uppercase tracking-[0.12em] text-muted-foreground mb-3">Your tickets</h2>

              {loadErr && <ErrorState description={loadErr} onRetry={load} />}
              {tickets === null && !loadErr && <LoadingState variant="inline" />}

              {tickets && tickets.length === 0 && (
                <div className={[VISUAL.bento, 'py-10 px-6 text-center text-[14px] text-muted-foreground'].join(' ')}>
                  No tickets yet. Use the form above to get in touch.
                </div>
              )}

              {tickets && tickets.length > 0 && (
                <section className={[VISUAL.bento, 'overflow-hidden'].join(' ')}>
                  <ul className="divide-y divide-border/40">
                    {tickets.map((t) => (
                      <li key={t.id}>
                        <button
                          type="button"
                          onClick={() => setSelected(t)}
                          className="w-full flex items-center gap-4 px-5 sm:px-6 py-4 hover:bg-muted/40 transition-colors text-left"
                        >
                          <div className="flex-1 min-w-0">
                            <p className="text-[14px] font-semibold text-foreground truncate">{t.subject}</p>
                            <p className="text-[12px] text-muted-foreground mt-0.5">
                              {TOPICS.find((x) => x.value === t.topic)?.label ?? t.topic} · {relativeTime(t.last_activity_at)}
                            </p>
                          </div>
                          <span className={statusPill(ticketTone(t.status))}>{t.status}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </section>
          </>
        )}
      </div>
    </div>
  );
}
